import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth, authFetch } from "../../App";
import { toast } from "sonner";
import {
  Hotel, Home, Plus, Trash2, Edit2, MapPin, Users, Calendar, Check,
  ChevronDown, ChevronUp, Upload, FileText, Loader2, Phone, ExternalLink,
  Sparkles, Eye, EyeOff, Hash, KeyRound, Clock, Utensils, BedDouble, Moon, Euro, Globe, Paperclip,
  X, RefreshCw, Download, AlertTriangle,
} from "lucide-react";

const API = "/api";

const EMPTY = {
  name: "",
  address: "",
  from_date: "",
  to_date: "",
  assigned_user_ids: [],
  notes: "",
  phone: "",
  gps_lat: null,
  gps_lng: null,
  checkin_from: "",
  checkin_to: "",
  checkout_from: "",
  checkout_to: "",
  booking_number: "",
  booking_pin: "",
  guest_name: "",
  meal_plan: "",
  room_count: null,
  nights: null,
  price: null,
  source_type: "manual",
  source_file: "",
  booking_provider: "",
  visible_fields: null, // null = Default-Set wird angewendet
};

// Felder, die der Mitarbeiter in der App sehen kann (Reihenfolge = Anzeigereihenfolge)
const DISPLAYABLE_FIELDS = [
  { key: "name", label: "Name", icon: Hotel },
  { key: "address", label: "Adresse", icon: MapPin },
  { key: "from_date", label: "Anreise (Datum)", icon: Calendar },
  { key: "to_date", label: "Abreise (Datum)", icon: Calendar },
  { key: "checkin_from", label: "Check-in ab", icon: Clock },
  { key: "checkout_to", label: "Check-out bis", icon: Clock },
  { key: "phone", label: "Telefon", icon: Phone },
  { key: "guest_name", label: "Gastname", icon: Users },
  { key: "booking_number", label: "Buchungsnummer", icon: Hash },
  { key: "booking_pin", label: "PIN / Sicherheitscode", icon: KeyRound },
  { key: "meal_plan", label: "Verpflegung", icon: Utensils },
  { key: "room_count", label: "Zimmer-Anzahl", icon: BedDouble },
  { key: "nights", label: "Nächte", icon: Moon },
  { key: "price", label: "Preis", icon: Euro },
  { key: "booking_provider", label: "Anbieter", icon: Globe },
  { key: "notes", label: "Notizen", icon: FileText },
];

// Default: was sieht der Mitarbeiter, wenn visible_fields=null?
export const DEFAULT_VISIBLE_FIELDS = [
  "name", "address", "from_date", "to_date",
  "checkin_from", "checkout_to", "phone", "guest_name",
  "booking_number", "meal_plan",
];

/**
 * Admin-Sektion für Unterkünfte (Hotels / Ferienwohnungen) im Projekt.
 * - Mehrere Einträge pro Projekt
 * - Booking-Bestätigung als PDF / JPG / PNG hochladen → automatische Datenextraktion
 * - Vorschau aller erkannten Felder editierbar vor dem Speichern
 * - Auswahl welche Felder der Mitarbeiter in der App sieht
 * API: GET/POST/PATCH/DELETE /api/projects/{id}/accommodations
 *      POST /api/projects/{id}/accommodations/parse-upload
 */
export default function AccommodationsAdmin({ projectId, users = [] }) {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);  // alle Unterkunfts-Finance-Entries dieses Projekts
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | accommodation_id
  const [form, setForm] = useState(EMPTY);
  const [parsing, setParsing] = useState(false);
  const [parsedFields, setParsedFields] = useState(new Set()); // welche Felder kamen vom Parser
  const [uncertainFields, setUncertainFields] = useState(new Set()); // unsichere Erkennungen (gelb statt grün)
  const [parsedRawText, setParsedRawText] = useState("");          // Debug für Admin: Rohtext
  const [parsedConfidenceMap, setParsedConfidenceMap] = useState({}); // Debug: vollständige Confidence
  const [parsedSourceFile, setParsedSourceFile] = useState(null);  // Original-Datei-Pfad
  const [lastUploadedFile, setLastUploadedFile] = useState(null);  // Re-Parse-Funktion
  const fileRef = useRef(null);
  // Detail-Modal & Quick-PDF-Preview
  const [detailAcc, setDetailAcc] = useState(null);   // Vollansicht
  const [previewAcc, setPreviewAcc] = useState(null); // PDF-Schnellvorschau

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const da = a.from_date || "9999-99-99";
      const db = b.from_date || "9999-99-99";
      return da.localeCompare(db);
    });
  }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accR, finR] = await Promise.all([
        authFetch(`${API}/projects/${projectId}/accommodations`),
        authFetch(`${API}/projects/${projectId}/finance-entries`),
      ]);
      if (accR.ok) setItems(await accR.json());
      if (finR.ok) {
        const data = await finR.json();
        const list = Array.isArray(data) ? data : (data.entries || []);
        setFinanceEntries(list.filter((e) => e.source_type === "accommodation"));
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [projectId]);

  // Map: accommodation_id → finance_entry (für Badge-Verifikation + Verlinkung)
  const entryByAccId = useMemo(() => {
    const m = {};
    for (const e of financeEntries) {
      if (e.source_id) m[e.source_id] = e;
    }
    return m;
  }, [financeEntries]);

  // Retry-Sync: forciert das Backend, eine Unterkunft erneut zu synchronisieren
  // (durch ein PATCH ohne inhaltliche Änderung — der Hook auf der Server-Seite
  // ruft sync_accommodation_finance erneut auf).
  const retrySync = useCallback(async (acc) => {
    try {
      const r = await authFetch(`${API}/projects/${projectId}/accommodations/${acc.id}`, {
        method: "PATCH",
        body: JSON.stringify({ price: acc.price }),
      });
      if (r.ok) {
        toast.success("Sync ausgelöst — Eintrag wird im Controlling erscheinen");
        await load();
      } else {
        toast.error("Sync fehlgeschlagen");
      }
    } catch {
      toast.error("Sync fehlgeschlagen");
    }
  }, [projectId, load]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ ...EMPTY, visible_fields: [...DEFAULT_VISIBLE_FIELDS] });
    setParsedFields(new Set());
    setUncertainFields(new Set());
    setEditing("new");
  };

  const startEdit = (acc) => {
    setForm({
      ...EMPTY,
      ...acc,
      assigned_user_ids: acc.assigned_user_ids || [],
      visible_fields: Array.isArray(acc.visible_fields) ? acc.visible_fields : [...DEFAULT_VISIBLE_FIELDS],
    });
    setParsedFields(new Set());
    setUncertainFields(new Set());
    setEditing(acc.id);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); setParsedFields(new Set()); setUncertainFields(new Set()); };

  const toggleUser = (uid) => {
    setForm(p => ({
      ...p,
      assigned_user_ids: (p.assigned_user_ids || []).includes(uid)
        ? p.assigned_user_ids.filter(x => x !== uid)
        : [...(p.assigned_user_ids || []), uid],
    }));
  };

  const toggleVisibleField = (key) => {
    setForm(p => {
      const cur = Array.isArray(p.visible_fields) ? p.visible_fields : [...DEFAULT_VISIBLE_FIELDS];
      return {
        ...p,
        visible_fields: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key],
      };
    });
  };

  const handleUploadClick = () => fileRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset for re-pick
    if (!file) return;
    // Neues Verhalten: NICHT mehr automatisch parsen — nur als Anhang merken und Form öffnen
    const max = 25 * 1024 * 1024;
    if (file.size > max) { toast.error("Datei ist größer als 25 MB"); return; }
    const okTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/jpg"];
    const okExt = /\.(pdf|jpe?g|png|webp)$/i.test(file.name);
    if (!okTypes.includes(file.type) && !okExt) {
      toast.error("Nur PDF, JPG oder PNG erlaubt");
      return;
    }
    setForm({ ...EMPTY, visible_fields: [...DEFAULT_VISIBLE_FIELDS] });
    setParsedFields(new Set());
    setUncertainFields(new Set());
    setParsedRawText("");
    setParsedConfidenceMap({});
    setParsedSourceFile(null);
    setLastUploadedFile(file);
    setEditing("new");
    toast.success(`"${file.name}" als Beleg angehängt — bitte Daten manuell eintragen oder PDF auswerten.`);
  };

  const suggestFromPdf = async () => {
    if (!lastUploadedFile) { toast.error("Keine PDF zum Auswerten vorhanden"); return; }
    await runParserOnFile(lastUploadedFile);
  };

  const reRunParser = async () => {
    if (!lastUploadedFile) { toast.error("Keine Datei zum erneuten Erkennen vorhanden"); return; }
    await runParserOnFile(lastUploadedFile);
  };

  const runParserOnFile = async (file) => {
    setParsing(true);
    toast.info("PDF wird ausgewertet …");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authFetch(`${API}/projects/${projectId}/accommodations/parse-upload`, {
        method: "POST", body: fd,
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        toast.error("Auswertung fehlgeschlagen: " + (errBody?.detail || r.status));
        setParsing(false);
        return;
      }
      const data = await r.json();
      const extracted = data?.extracted || {};
      const confidence = extracted._confidence || {};
      const detected = new Set();
      const uncertain = new Set();
      // Werte nur in LEERE Felder übernehmen — bestehende manuelle Eingaben nicht überschreiben
      setForm(p => {
        const next = { ...p };
        for (const [k, v] of Object.entries(extracted)) {
          if (k.startsWith("_")) continue;
          if (v === null || v === undefined || v === "") continue;
          if (!(k in next)) continue;
          const cur = next[k];
          if (cur === "" || cur === null || cur === undefined) {
            next[k] = v;
            detected.add(k);
            if (confidence[k] === "uncertain") uncertain.add(k);
          }
        }
        return next;
      });
      setParsedFields(detected);
      setUncertainFields(uncertain);
      setParsedRawText(extracted._raw_text_preview || "");
      setParsedConfidenceMap(confidence);
      setParsedSourceFile(extracted.source_file || null);
      const recognizedCount = detected.size - uncertain.size;
      if (recognizedCount > 0 && uncertain.size > 0) {
        toast.success(`${recognizedCount} Felder ergänzt · ${uncertain.size} bitte prüfen`);
      } else if (recognizedCount > 0) {
        toast.success(`${recognizedCount} leere Felder vorgeschlagen — bitte prüfen`);
      } else {
        toast.warning("Keine zusätzlichen Felder erkannt");
      }
    } catch (err) {
      console.error(err);
      toast.error("Fehler bei der Auswertung: " + (err?.message || "unknown"));
    } finally {
      setParsing(false);
    }
  };

  const openOriginalFile = () => {
    if (!parsedSourceFile) { toast.info("Keine Original-Datei verfügbar"); return; }
    const url = `${API}/projects/${projectId}/accommodations/source-file?path=${encodeURIComponent(parsedSourceFile)}`;
    window.open(url, "_blank");
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name ist Pflicht"); return; }
    // Cleanup: numerische Felder
    const payload = { ...form };
    ["room_count", "nights"].forEach(k => {
      if (payload[k] === "" || payload[k] === null || payload[k] === undefined) payload[k] = null;
      else payload[k] = parseInt(payload[k], 10) || null;
    });
    ["gps_lat", "gps_lng", "price"].forEach(k => {
      if (payload[k] === "" || payload[k] === null || payload[k] === undefined) payload[k] = null;
      else payload[k] = parseFloat(payload[k]) || null;
    });

    try {
      const isNew = editing === "new";
      const url = isNew
        ? `${API}/projects/${projectId}/accommodations`
        : `${API}/projects/${projectId}/accommodations/${editing}`;
      const r = await authFetch(url, {
        method: isNew ? "POST" : "PATCH",
        body: JSON.stringify(payload),
      });
      if (!r.ok) { toast.error("Speichern fehlgeschlagen"); return; }
      const savedAcc = await r.json().catch(() => ({}));
      const accId = savedAcc?.id || (typeof editing === "string" && editing !== "new" ? editing : null);
      // PDF-Beleg anhängen, falls vorhanden und Eintrag NEU war
      if (isNew && lastUploadedFile && accId) {
        try {
          const fd = new FormData();
          fd.append("file", lastUploadedFile);
          await authFetch(`${API}/projects/${projectId}/accommodations/${accId}/attach-document`, {
            method: "POST", body: fd,
          });
        } catch (e) {
          console.warn("PDF-Anhang konnte nicht gespeichert werden", e);
        }
      }
      toast.success(isNew ? "Unterkunft hinzugefügt" : "Unterkunft aktualisiert");
      cancel();
      await load();
    } catch (e) {
      toast.error("Fehler: " + (e?.message || "unknown"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Unterkunft wirklich löschen?")) return;
    try {
      const r = await authFetch(`${API}/projects/${projectId}/accommodations/${id}`, { method: "DELETE" });
      if (r.ok) { toast.success("Gelöscht"); await load(); }
      else toast.error("Löschen fehlgeschlagen");
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <div className="space-y-3" data-testid="accommodations-admin">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Hotel className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-semibold">Unterkünfte</span>
          <span className="text-xs text-gray-500">({items.length})</span>
        </div>
        {editing === null && (
          <div className="flex items-center gap-1.5">
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
              onChange={handleFileSelected}
              className="hidden"
              data-testid="acc-upload-input"
            />
            <button
              type="button"
              onClick={startNew}
              className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center gap-1.5"
              data-testid="acc-add-btn"
              title="Unterkunft manuell eintragen"
            >
              <Plus className="w-3.5 h-3.5" /> Unterkunft manuell
            </button>
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={parsing}
              className="text-xs px-3 py-1.5 rounded-md border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 font-semibold flex items-center gap-1.5 disabled:opacity-60"
              title="Booking-PDF / Foto als Beleg anhängen (ohne automatische Erkennung)"
              data-testid="acc-upload-btn"
            >
              {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
              {parsing ? "Anhängen…" : "Booking PDF anhängen"}
            </button>
          </div>
        )}
      </div>

      {/* Form (Add / Edit / Booking-Vorschau) */}
      {editing !== null && (
        <AccommodationForm
          form={form}
          setForm={setForm}
          parsedFields={parsedFields}
          uncertainFields={uncertainFields}
          users={users}
          toggleUser={toggleUser}
          toggleVisibleField={toggleVisibleField}
          onCancel={cancel}
          onSave={save}
          isPreview={parsedFields.size > 0}
          onReParse={reRunParser}
          onOpenOriginal={openOriginalFile}
          onSuggestFromPdf={suggestFromPdf}
          attachedFileName={lastUploadedFile?.name || null}
          parsedRawText={parsedRawText}
          parsedConfidenceMap={parsedConfidenceMap}
          parsing={parsing}
        />
      )}

      {/* List */}
      {loading ? (
        <div className="text-xs text-gray-500">Lade...</div>
      ) : items.length === 0 && editing === null ? (
        <EmptyState onAdd={startNew} onUpload={handleUploadClick} parsing={parsing} />
      ) : (
        <div
          className="grid gap-3 items-start"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))" }}
          data-testid="acc-list"
        >
          {sortedItems.map(acc => (
            <AccommodationCard
              key={acc.id}
              acc={{ ...acc, project_id: projectId }}
              users={users}
              currentUserId={currentUser?.id}
              financeEntry={entryByAccId[acc.id] || null}
              onRetrySync={retrySync}
              onEdit={() => startEdit(acc)}
              onDelete={() => remove(acc.id)}
              onOpenDetail={(a) => setDetailAcc(a)}
              onPreviewPdf={(a) => setPreviewAcc(a)}
            />
          ))}
        </div>
      )}

      {/* Detail-Modal (Vollansicht inkl. PDF-Verwaltung) */}
      {detailAcc && (
        <AccommodationDetailModal
          acc={detailAcc}
          projectId={projectId}
          users={users}
          financeEntry={entryByAccId[detailAcc.id] || null}
          isAdmin={(currentUser?.role || "").toLowerCase() === "admin" || (currentUser?.role || "").toLowerCase() === "projektleiter"}
          onClose={() => setDetailAcc(null)}
          onEdit={() => { const a = detailAcc; setDetailAcc(null); startEdit(a); }}
          onDelete={async () => { const a = detailAcc; setDetailAcc(null); await remove(a.id); }}
          onPreviewPdf={() => setPreviewAcc(detailAcc)}
          onRetrySync={retrySync}
          onChanged={async () => {
            await load();
            // detailAcc nach Reload aktualisieren
            const r = await authFetch(`${API}/projects/${projectId}/accommodations`);
            if (r.ok) {
              const list = await r.json();
              const fresh = list.find(x => x.id === detailAcc.id);
              if (fresh) setDetailAcc({ ...fresh, project_id: projectId });
            }
          }}
        />
      )}

      {/* PDF-Schnellvorschau-Overlay */}
      {previewAcc && (
        <PdfPreviewOverlay
          acc={previewAcc}
          projectId={projectId}
          onClose={() => setPreviewAcc(null)}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Form ───────────────────────── */

// IMPORTANT: Field must be defined OUTSIDE AccommodationForm to avoid losing input focus
// on every keystroke (each parent re-render would otherwise create a new Field component).
function Field({ name, label, type = "text", placeholder, fullWidth = false, step, form, setForm, parsedFields, uncertainFields }) {
  const detected = parsedFields?.has(name);
  const uncertain = uncertainFields?.has(name);
  const val = form[name] ?? "";
  // Wenn das Feld vom Parser NICHT erkannt wurde → keine Dummy-Placeholder anzeigen
  const safePlaceholder = (parsedFields && parsedFields.size > 0 && !detected) ? "" : placeholder;
  // Color je nach Confidence
  const ringClass = uncertain
    ? "border-amber-400 bg-amber-50/30"
    : detected
      ? "border-emerald-300 bg-emerald-50/40"
      : "";
  return (
    <label className={`text-xs ${fullWidth ? "sm:col-span-2" : ""}`}>
      <span className="mb-0.5 text-gray-500 flex items-center gap-1">
        {label}
        {detected && !uncertain && (
          <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">
            <Sparkles className="w-2.5 h-2.5" /> erkannt
          </span>
        )}
        {uncertain && (
          <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">
            <Sparkles className="w-2.5 h-2.5" /> prüfen
          </span>
        )}
      </span>
      <input
        type={type}
        step={step}
        value={val === null ? "" : val}
        onChange={(e) => setForm(p => ({ ...p, [name]: e.target.value }))}
        placeholder={safePlaceholder}
        className={`w-full px-2 py-1.5 rounded-md border bg-transparent text-sm ${ringClass}`}
        data-testid={`acc-form-${name}`}
      />
    </label>
  );
}

function AccommodationForm({ form, setForm, parsedFields, uncertainFields, users, toggleUser, toggleVisibleField, onCancel, onSave, isPreview, onReParse, onOpenOriginal, onSuggestFromPdf, attachedFileName, parsedRawText, parsedConfidenceMap, parsing }) {
  const [debugOpen, setDebugOpen] = useState(false);
  const isAdmin = (() => {
    try { return (JSON.parse(localStorage.getItem("user") || "{}").role || "").toLowerCase() === "admin"; }
    catch { return false; }
  })();

  const visibleSet = useMemo(
    () => new Set(Array.isArray(form.visible_fields) ? form.visible_fields : DEFAULT_VISIBLE_FIELDS),
    [form.visible_fields]
  );

  // Stable Field reference: just pre-bind the form context props once
  // (deps include form so prop values update, but the wrapper itself is a closure
  // that React still resolves to the top-level Field component — no remount)
  const fieldProps = { form, setForm, parsedFields, uncertainFields };

  return (
    <div className="rounded-lg border border-blue-500/20 bg-blue-500/[0.04] p-3 space-y-3" data-testid="acc-form">
      {/* PDF angehängt-Banner (manueller Workflow ist Hauptweg) */}
      {attachedFileName && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Paperclip className="w-4 h-4 flex-shrink-0" />
            <span className="font-semibold truncate">{attachedFileName}</span>
            <span className="text-blue-700 hidden sm:inline">angehängt</span>
          </div>
          <div className="flex items-center gap-1.5">
            {onOpenOriginal && (
              <button
                type="button"
                onClick={onOpenOriginal}
                className="text-[11px] px-2 py-1 rounded border border-blue-300 bg-white hover:bg-blue-100"
                data-testid="acc-form-open-pdf"
              >
                Booking PDF öffnen
              </button>
            )}
            {onSuggestFromPdf && (
              <button
                type="button"
                onClick={onSuggestFromPdf}
                disabled={parsing}
                className="text-[11px] px-2 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold disabled:opacity-60"
                data-testid="acc-form-suggest-pdf"
                title="Optional: KI schlägt Daten aus dem PDF vor. Daten werden nur in leere Felder eingetragen."
              >
                {parsing ? "Werte aus…" : "Daten aus PDF vorschlagen"}
              </button>
            )}
          </div>
        </div>
      )}

      {isPreview && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-start gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Vorschläge übernommen — bitte alle Felder prüfen</div>
            <div className="text-emerald-700 mt-0.5">
              {parsedFields.size - (uncertainFields?.size || 0)} Felder ergänzt (grün)
              {uncertainFields?.size > 0 && ` · ${uncertainFields.size} unsicher (gelb)`}
              . Leere Felder bitte manuell ergänzen.
            </div>
          </div>
        </div>
      )}

      {/* Pflicht / Grunddaten */}
      <SectionLabel>Grunddaten</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field {...fieldProps} name="name" label="Name *" placeholder="Hotel Bayerischer Hof" fullWidth />
        <Field {...fieldProps} name="address" label="Adresse" placeholder="Promenadeplatz 2-6, 80333 München" fullWidth />
        <Field {...fieldProps} name="from_date" label="Anreise" type="date" />
        <Field {...fieldProps} name="to_date" label="Abreise" type="date" />
        <Field {...fieldProps} name="checkin_from" label="Check-in ab (HH:MM)" type="time" />
        <Field {...fieldProps} name="checkout_to" label="Check-out bis (HH:MM)" type="time" />
      </div>

      {/* Booking-Details */}
      <SectionLabel>Buchungsdaten</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field {...fieldProps} name="booking_number" label="Buchungsnummer" placeholder="1234567890" />
        <Field {...fieldProps} name="booking_pin" label="PIN" placeholder="1234" />
        <Field {...fieldProps} name="guest_name" label="Gastname" placeholder="Max Mustermann" />
        <Field {...fieldProps} name="phone" label="Telefon" placeholder="+49 89 12345-0" />
        <Field {...fieldProps} name="meal_plan" label="Verpflegung" placeholder="Frühstück, HP, Selbstversorger…" />
        <Field {...fieldProps} name="booking_provider" label="Anbieter" placeholder="booking.com, direkt …" />
        <Field {...fieldProps} name="room_count" label="Zimmer" type="number" />
        <Field {...fieldProps} name="nights" label="Nächte" type="number" />
        <Field {...fieldProps} name="price" label="Preis (€)" type="number" step="0.01" />
      </div>

      {/* GPS */}
      <SectionLabel>GPS-Koordinaten (optional)</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field {...fieldProps} name="gps_lat" label="Latitude" type="number" step="0.000001" placeholder="48.137154" />
        <Field {...fieldProps} name="gps_lng" label="Longitude" type="number" step="0.000001" placeholder="11.576124" />
      </div>

      {/* Mitarbeiter */}
      <SectionLabel>Zugewiesene Mitarbeiter ({form.assigned_user_ids?.length || 0})</SectionLabel>
      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
        {users.length === 0 && <span className="text-xs text-gray-500">Keine Mitarbeiter geladen</span>}
        {users.map(u => {
          const sel = (form.assigned_user_ids || []).includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => toggleUser(u.id)}
              className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                sel ? "bg-blue-500/20 border-blue-500/40 text-blue-700" : "bg-transparent border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`acc-form-user-${u.id}`}
            >
              {sel && <Check className="w-3 h-3 inline mr-1" />}
              {u.name || u.email}
            </button>
          );
        })}
      </div>

      {/* Sichtbarkeit für Mitarbeiter */}
      <SectionLabel>
        <span className="flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" />
          Was sieht der Mitarbeiter in der App?
        </span>
      </SectionLabel>
      <div className="rounded-md bg-white/40 border border-gray-200 p-2.5">
        <div className="text-[11px] text-gray-500 mb-2">
          Wähle, welche Informationen den zugewiesenen Mitarbeitern in ihrer Projekt-Ansicht angezeigt werden.
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {DISPLAYABLE_FIELDS.map(({ key, label, icon: Icon }) => {
            const checked = visibleSet.has(key);
            return (
              <label
                key={key}
                className={`flex items-center gap-1.5 text-[11px] px-2 py-1.5 rounded-md border cursor-pointer transition-colors ${
                  checked ? "bg-blue-500/10 border-blue-500/30 text-blue-700" : "bg-transparent border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
                data-testid={`acc-form-visible-${key}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleVisibleField(key)}
                  className="w-3 h-3"
                />
                <Icon className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Notizen */}
      <label className="text-xs block">
        <span className="block mb-0.5 text-gray-500">Notizen</span>
        <textarea
          rows={2}
          value={form.notes || ""}
          onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
          placeholder="Frühstück inklusive, Check-in ab 15:00 …"
          className="w-full px-2 py-1.5 rounded-md border bg-transparent text-sm"
          data-testid="acc-form-notes"
        />
      </label>

      {/* Admin Debug Panel */}
      {isAdmin && isPreview && (
        <div className="rounded-md border border-amber-300/40 bg-amber-50/30 dark:bg-amber-950/20">
          <button
            type="button"
            onClick={() => setDebugOpen(o => !o)}
            className="w-full px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 flex items-center justify-between"
            data-testid="acc-form-debug-toggle"
          >
            <span>🔧 Parser-Debug (nur Admin)</span>
            <span>{debugOpen ? "▼" : "▶"}</span>
          </button>
          {debugOpen && (
            <div className="p-3 pt-1 space-y-2 text-xs" data-testid="acc-form-debug-panel">
              <div>
                <p className="font-semibold mb-1">Confidence-Map:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(parsedConfidenceMap || {}).map(([k, v]) => (
                    <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                      v === "recognized" ? "bg-emerald-100 text-emerald-800" :
                        v === "uncertain" ? "bg-amber-100 text-amber-800" :
                          "bg-gray-100 text-gray-700"
                    }`}>{k}: {v}</span>
                  ))}
                  {Object.keys(parsedConfidenceMap || {}).length === 0 && <span className="opacity-50">— keine Daten —</span>}
                </div>
              </div>
              {parsedRawText && (
                <div>
                  <p className="font-semibold mb-1">Roh-Text (erste 800 Zeichen):</p>
                  <pre className="bg-gray-900/80 text-gray-100 p-2 rounded text-[10px] whitespace-pre-wrap max-h-40 overflow-auto">{parsedRawText}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="text-xs px-3 py-1.5 rounded-md border" data-testid="acc-form-cancel">
          Abbrechen
        </button>
        <button
          type="button"
          onClick={onSave}
          className="text-xs px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          data-testid="acc-form-save"
        >
          Speichern
        </button>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold pt-1.5 border-t border-gray-200/40 mt-2 first:mt-0 first:border-t-0 first:pt-0">
      {children}
    </div>
  );
}

/* ───────────────────────── Sub-Components ───────────────────────── */

function EmptyState({ onAdd, onUpload, parsing }) {
  return (
    <div
      className="rounded-xl border border-dashed border-blue-300/50 bg-blue-50/30 px-5 py-6 flex flex-col items-center text-center max-w-2xl"
      data-testid="acc-empty-state"
    >
      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-2">
        <Hotel className="w-5 h-5 text-blue-500" />
      </div>
      <div className="text-sm font-semibold text-gray-700">Keine Unterkünfte hinterlegt</div>
      <div className="text-xs text-gray-500 mt-0.5 mb-3">
        Booking-Bestätigung (PDF, JPG, PNG) hochladen — Daten werden automatisch erkannt.
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onUpload}
          disabled={parsing}
          className="text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center gap-1.5 disabled:opacity-60"
          data-testid="acc-empty-upload-btn"
        >
          {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Booking hochladen
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="text-xs px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white font-semibold flex items-center gap-1"
          data-testid="acc-empty-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Manuell anlegen
        </button>
      </div>
    </div>
  );
}

function AccommodationCard({ acc, users, currentUserId, financeEntry, onRetrySync, onEdit, onDelete, onOpenDetail, onPreviewPdf }) {
  const [showDetails, setShowDetails] = useState(false);
  const assignedIds = acc.assigned_user_ids || [];
  const isMine = currentUserId && assignedIds.includes(currentUserId);

  const lower = (acc.name || "").toLowerCase();
  const isApartment = /ferien|wohnung|apartment|fewo/.test(lower);
  const Icon = isApartment ? Home : Hotel;

  const assignedUsers = assignedIds
    .map(uid => users.find(u => u.id === uid))
    .filter(Boolean);

  const formatDate = (d) => {
    if (!d) return "";
    try {
      const [y, m, day] = d.split("-");
      return `${day}.${m}.${y}`;
    } catch { return d; }
  };

  const dateStr = (acc.from_date || acc.to_date)
    ? `${formatDate(acc.from_date) || "—"} → ${formatDate(acc.to_date) || "—"}`
    : "Kein Zeitraum";

  // Stats
  const hasBookingMeta = acc.booking_number || acc.booking_pin || acc.guest_name || acc.meal_plan || acc.phone;
  const visibleCount = Array.isArray(acc.visible_fields) ? acc.visible_fields.length : DEFAULT_VISIBLE_FIELDS.length;
  const hasPdf = !!acc.document_path;

  // Stop-Propagation Helper für Icon-Klicks (verhindert Öffnen des Detail-Modals)
  const stop = (handler) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    handler?.();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetail && onOpenDetail(acc)}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onOpenDetail) { e.preventDefault(); onOpenDetail(acc); } }}
      className={`relative rounded-xl border-2 px-3 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-lg max-w-[420px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 ${
        isMine
          ? "border-emerald-400 bg-emerald-50/40"
          : isApartment
            ? "border-emerald-200/60 bg-white hover:border-emerald-300"
            : "border-blue-200/60 bg-white hover:border-blue-300"
      }`}
      style={isMine ? { boxShadow: "0 0 0 3px rgba(16,185,129,0.12)" } : undefined}
      data-testid={`acc-item-${acc.id}`}
    >
      {isMine && (
        <span className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white">
          DU
        </span>
      )}

      <div className="flex items-start gap-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isApartment ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"
        }`}>
          <Icon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {acc.name || "(ohne Name)"}
            </div>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
              isApartment ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
            }`}>
              {isApartment ? "Ferienwohnung" : "Hotel"}
            </span>
            {acc.source_type && acc.source_type !== "manual" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> {acc.source_type === "pdf" ? "PDF" : "Bild"}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 text-[11px] text-gray-600 mt-0.5">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            <span className="font-mono">{dateStr}</span>
            {(acc.checkin_from || acc.checkout_to) && (
              <span className="text-gray-400 ml-1">
                {acc.checkin_from && `ab ${acc.checkin_from}`}
                {acc.checkin_from && acc.checkout_to && " / "}
                {acc.checkout_to && `bis ${acc.checkout_to}`}
              </span>
            )}
          </div>

          {/* Controlling-Hinweis (nur wenn Finance-Entry wirklich existiert) */}
          {financeEntry && financeEntry.payment_status !== "cancelled" && (
            <div className="mt-1">
              <a
                href={`/app/projekte/${acc.project_id}/controlling?finance_entry=${financeEntry.id}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:text-amber-900"
                title={`Im Controlling anzeigen: ${Number(financeEntry.amount_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`}
                data-testid={`acc-finance-badge-${acc.id}`}
              >
                <Euro className="w-2.5 h-2.5" />
                Ausgabe im Controlling erfasst
                <span className="font-mono opacity-80">
                  · {Number(financeEntry.amount_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                </span>
              </a>
            </div>
          )}
          {/* Preis > 0 aber KEIN Finance-Entry → Warnung + Retry */}
          {!financeEntry && acc.price != null && Number(acc.price) > 0 && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-50 text-red-700 border border-red-200"
                title="Diese Unterkunft hat einen Preis, aber es konnte keine Ausgabe im Controlling angelegt werden."
                data-testid={`acc-finance-warning-${acc.id}`}
              >
                <AlertTriangle className="w-2.5 h-2.5" /> Ausgabe konnte nicht ins Controlling übernommen werden
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRetrySync?.(acc); }}
                className="text-[10px] px-1.5 py-0.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center gap-1"
                data-testid={`acc-finance-retry-${acc.id}`}
              >
                <RefreshCw className="w-2.5 h-2.5" /> Erneut übernehmen
              </button>
            </div>
          )}
          {/* Cancelled finance entry → Hinweis */}
          {financeEntry && financeEntry.payment_status === "cancelled" && (
            <div className="mt-1">
              <span
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 border border-gray-300 italic"
                title={financeEntry.auto_cancel_reason || "Ausgabe storniert"}
                data-testid={`acc-finance-cancelled-${acc.id}`}
              >
                Ausgabe storniert
              </span>
            </div>
          )}

          {/* Mitarbeiter */}
          <div className="flex items-center gap-1.5 mt-1.5">
            {assignedUsers.length === 0 ? (
              <span className="text-[11px] text-amber-600 italic">Keine Zuordnung</span>
            ) : assignedUsers.length === 1 ? (
              <>
                <Avatar user={assignedUsers[0]} size={20} />
                <span className="text-[11px] text-gray-700 truncate">{assignedUsers[0].name || assignedUsers[0].email}</span>
              </>
            ) : (
              <>
                <AvatarStack users={assignedUsers.slice(0, 4)} extra={Math.max(0, assignedUsers.length - 4)} />
                <span className="text-[11px] text-gray-700 font-medium">
                  {assignedUsers.length} MA
                </span>
              </>
            )}
          </div>

          {/* Details Toggle (inline) */}
          {(acc.address || hasBookingMeta || acc.notes) && (
            <button
              type="button"
              onClick={stop(() => setShowDetails(s => !s))}
              className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 mt-1"
              data-testid={`acc-toggle-details-${acc.id}`}
            >
              <span>Details</span>
              {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}

          {showDetails && (
            <div className="mt-1 space-y-0.5 text-[11px]">
              {acc.address && (
                <div className="flex items-start gap-1 text-gray-600">
                  <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span className="italic">{acc.address}</span>
                </div>
              )}
              {acc.phone && (
                <div className="flex items-center gap-1 text-gray-600">
                  <Phone className="w-3 h-3" /> <a href={`tel:${acc.phone}`} className="hover:underline">{acc.phone}</a>
                </div>
              )}
              {acc.booking_number && (
                <div className="flex items-center gap-1 text-gray-600">
                  <Hash className="w-3 h-3" /> <span className="font-mono">{acc.booking_number}</span>
                  {acc.booking_pin && <span className="text-gray-400">· PIN {acc.booking_pin}</span>}
                </div>
              )}
              {acc.guest_name && (
                <div className="flex items-center gap-1 text-gray-600">
                  <Users className="w-3 h-3" /> {acc.guest_name}
                </div>
              )}
              {acc.meal_plan && (
                <div className="flex items-center gap-1 text-gray-600">
                  <Utensils className="w-3 h-3" /> {acc.meal_plan}
                </div>
              )}
              {(acc.room_count || acc.nights || acc.price) && (
                <div className="flex items-center gap-2 text-gray-500">
                  {acc.room_count != null && <span><BedDouble className="w-3 h-3 inline" /> {acc.room_count} Zi.</span>}
                  {acc.nights != null && <span><Moon className="w-3 h-3 inline" /> {acc.nights} N.</span>}
                  {acc.price != null && <span><Euro className="w-3 h-3 inline" /> {Number(acc.price).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</span>}
                </div>
              )}
              {acc.notes && (
                <div className="text-gray-500 italic">Notiz: {acc.notes}</div>
              )}
              <div className="flex items-center gap-1 text-gray-400 pt-1 border-t border-gray-200/60 mt-1">
                <Eye className="w-3 h-3" /> Mitarbeiter sieht {visibleCount} Felder
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-0.5 flex-shrink-0">
          {hasPdf && (
            <button
              type="button"
              onClick={stop(() => onPreviewPdf?.(acc))}
              className="p-1 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700"
              title={`PDF Schnellvorschau: ${acc.document_filename || "Beleg"}`}
              data-testid={`acc-pdf-preview-${acc.id}`}
            >
              <FileText className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={stop(onEdit)}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            title="Bearbeiten"
            data-testid={`acc-edit-${acc.id}`}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={stop(onDelete)}
            className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600"
            title="Löschen"
            data-testid={`acc-delete-${acc.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ user, size = 24 }) {
  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map(s => s[0]?.toUpperCase()).join("");
  const hue = ((user?.id || user?.email || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % 360;
  return (
    <div
      className="rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0"
      style={{
        width: size, height: size,
        fontSize: size * 0.42,
        background: `hsl(${hue} 60% 50%)`,
      }}
      title={user?.name || user?.email}
    >
      {initials || "?"}
    </div>
  );
}

function AvatarStack({ users, extra = 0 }) {
  return (
    <div className="flex -space-x-1.5">
      {users.map(u => (
        <div key={u.id} className="ring-2 ring-white rounded-full">
          <Avatar user={u} size={22} />
        </div>
      ))}
      {extra > 0 && (
        <div
          className="w-[22px] h-[22px] rounded-full bg-gray-200 text-gray-700 ring-2 ring-white text-[9px] font-bold flex items-center justify-center"
          title={`+${extra} weitere`}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}


/* ───────────────────────── Detail Modal (Full View) ───────────────────────── */

function AccommodationDetailModal({ acc, projectId, users, financeEntry, isAdmin, onClose, onEdit, onDelete, onPreviewPdf, onRetrySync, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const assignedUsers = (acc.assigned_user_ids || [])
    .map(uid => users.find(u => u.id === uid))
    .filter(Boolean);

  const lower = (acc.name || "").toLowerCase();
  const isApartment = /ferien|wohnung|apartment|fewo/.test(lower);
  const Icon = isApartment ? Home : Hotel;

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const [y, m, day] = d.split("-");
      return `${day}.${m}.${y}`;
    } catch { return d; }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  // Body-Scroll blockieren solange Modal offen
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC schließt Modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleUploadPdf = () => fileRef.current?.click();

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/jpg"];
    const okExt = /\.(pdf|jpe?g|png|webp)$/i.test(file.name);
    if (!okTypes.includes(file.type) && !okExt) {
      toast.error("Nur PDF, JPG oder PNG erlaubt");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Datei ist größer als 25 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authFetch(`${API}/projects/${projectId}/accommodations/${acc.id}/attach-document`, {
        method: "POST", body: fd,
      });
      if (!r.ok) {
        toast.error("Hochladen fehlgeschlagen");
        return;
      }
      toast.success(acc.document_path ? "PDF ersetzt" : "PDF gespeichert");
      await onChanged?.();
    } catch (err) {
      toast.error("Fehler beim Upload: " + (err?.message || "unknown"));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePdf = async () => {
    if (!window.confirm("Booking-PDF wirklich entfernen?")) return;
    try {
      const r = await authFetch(`${API}/projects/${projectId}/accommodations/${acc.id}/attach-document`, {
        method: "DELETE",
      });
      if (!r.ok) {
        toast.error("PDF konnte nicht entfernt werden");
        return;
      }
      toast.success("PDF entfernt");
      await onChanged?.();
    } catch (err) {
      toast.error("Fehler: " + (err?.message || "unknown"));
    }
  };

  const handleDownloadPdf = async () => {
    if (!acc.document_path) return;
    try {
      const r = await authFetch(`${API}/projects/${projectId}/accommodations/source-file?path=${encodeURIComponent(acc.document_path)}`);
      if (!r.ok) { toast.error("Download fehlgeschlagen"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = acc.document_filename || `booking-${acc.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Fehler beim Download");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="acc-detail-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isApartment ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-gray-900 truncate">{acc.name || "(ohne Name)"}</h2>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  isApartment ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                }`}>
                  {isApartment ? "Ferienwohnung" : "Hotel"}
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-0.5 font-mono">
                {formatDate(acc.from_date)} → {formatDate(acc.to_date)}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex-shrink-0"
            title="Schließen"
            data-testid="acc-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Booking-PDF Section */}
          <section className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-3">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">Booking-PDF / Beleg</span>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleFileChosen}
                className="hidden"
                data-testid="acc-detail-pdf-input"
              />
            </div>

            {acc.document_path ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs bg-white rounded-md p-2 border border-blue-200">
                  <Paperclip className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  <span className="font-semibold text-gray-800 truncate flex-1">
                    {acc.document_filename || "Beleg"}
                  </span>
                  {acc.document_uploaded_at && (
                    <span className="text-[10px] text-gray-500 flex-shrink-0">
                      hochgeladen {formatDateTime(acc.document_uploaded_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={onPreviewPdf}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center gap-1.5"
                    data-testid="acc-detail-pdf-view"
                  >
                    <Eye className="w-3.5 h-3.5" /> Anzeigen
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    className="text-xs px-2.5 py-1.5 rounded-md border border-blue-300 bg-white hover:bg-blue-50 text-blue-700 flex items-center gap-1.5"
                    data-testid="acc-detail-pdf-download"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        onClick={handleUploadPdf}
                        disabled={uploading}
                        className="text-xs px-2.5 py-1.5 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="acc-detail-pdf-replace"
                      >
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Ersetzen
                      </button>
                      <button
                        type="button"
                        onClick={handleDeletePdf}
                        className="text-xs px-2.5 py-1.5 rounded-md border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 flex items-center gap-1.5"
                        data-testid="acc-detail-pdf-delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Entfernen
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-600 space-y-2">
                <div className="italic">Kein Booking-PDF angehängt.</div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={handleUploadPdf}
                    disabled={uploading}
                    className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="acc-detail-pdf-upload"
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? "Lädt hoch…" : "PDF hochladen"}
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Grunddaten */}
          <DetailSection title="Grunddaten">
            <DetailRow icon={MapPin} label="Adresse" value={acc.address} />
            <DetailRow icon={Calendar} label="Anreise" value={formatDate(acc.from_date)} />
            <DetailRow icon={Calendar} label="Abreise" value={formatDate(acc.to_date)} />
            <DetailRow icon={Clock} label="Check-in ab" value={acc.checkin_from} />
            <DetailRow icon={Clock} label="Check-out bis" value={acc.checkout_to} />
            <DetailRow icon={Phone} label="Telefon" value={acc.phone} link={acc.phone ? `tel:${acc.phone}` : null} />
          </DetailSection>

          {/* Buchungsdaten */}
          <DetailSection title="Buchungsdaten">
            <DetailRow icon={Hash} label="Buchungsnummer" value={acc.booking_number} mono />
            <DetailRow icon={KeyRound} label="PIN / Sicherheitscode" value={acc.booking_pin} mono />
            <DetailRow icon={Users} label="Gastname" value={acc.guest_name} />
            <DetailRow icon={Utensils} label="Verpflegung" value={acc.meal_plan} />
            <DetailRow icon={Globe} label="Anbieter" value={acc.booking_provider} />
            <DetailRow icon={BedDouble} label="Zimmer" value={acc.room_count != null ? `${acc.room_count}` : null} />
            <DetailRow icon={Moon} label="Nächte" value={acc.nights != null ? `${acc.nights}` : null} />
            <DetailRow
              icon={Euro}
              label="Preis"
              value={acc.price != null ? `${Number(acc.price).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : null}
            />
          </DetailSection>

          {/* Controlling-Verknüpfung */}
          {financeEntry && financeEntry.payment_status !== "cancelled" && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-2 flex-wrap"
              data-testid="acc-detail-finance-hint"
            >
              <div className="flex items-center gap-2 text-xs text-amber-800">
                <Euro className="w-4 h-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">Ausgabe im Controlling erfasst</div>
                  <div className="text-amber-700">
                    Betrag: <span className="font-mono font-semibold">{Number(financeEntry.amount_gross || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span> · Kategorie: Unterkunft
                  </div>
                </div>
              </div>
              <a
                href={`/app/projekte/${projectId}/controlling?finance_entry=${financeEntry.id}`}
                className="text-xs px-2.5 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center gap-1.5"
                data-testid="acc-detail-finance-link"
              >
                Im Controlling anzeigen →
              </a>
            </div>
          )}
          {!financeEntry && acc.price != null && Number(acc.price) > 0 && (
            <div
              className="rounded-md border border-red-300 bg-red-50 p-3 flex items-center justify-between gap-2 flex-wrap"
              data-testid="acc-detail-finance-warning"
            >
              <div className="flex items-start gap-2 text-xs text-red-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Ausgabe konnte nicht ins Controlling übernommen werden</div>
                  <div className="text-red-700">
                    Bitte erneut übernehmen oder den Preis prüfen.
                  </div>
                </div>
              </div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => onRetrySync?.(acc)}
                  className="text-xs px-2.5 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center gap-1.5"
                  data-testid="acc-detail-finance-retry"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Erneut übernehmen
                </button>
              )}
            </div>
          )}
          {financeEntry && financeEntry.payment_status === "cancelled" && (
            <div
              className="rounded-md border border-gray-300 bg-gray-100 p-3 text-xs text-gray-700 italic"
              data-testid="acc-detail-finance-cancelled"
            >
              {financeEntry.auto_cancel_reason || "Ausgabe storniert"}
            </div>
          )}

          {/* Mitarbeiter */}
          <DetailSection title={`Zugewiesene Mitarbeiter (${assignedUsers.length})`}>
            {assignedUsers.length === 0 ? (
              <div className="text-xs text-amber-600 italic px-2">Keine Zuordnung</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 px-2">
                {assignedUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full pl-1 pr-2 py-0.5">
                    <Avatar user={u} size={18} />
                    <span className="text-[11px] text-gray-700">{u.name || u.email}</span>
                  </div>
                ))}
              </div>
            )}
          </DetailSection>

          {/* Notizen */}
          {acc.notes && (
            <DetailSection title="Notizen">
              <div className="text-xs text-gray-700 italic whitespace-pre-wrap px-2">{acc.notes}</div>
            </DetailSection>
          )}
        </div>

        {/* Footer Actions */}
        {isAdmin && (
          <div className="flex items-center justify-end gap-2 p-3 border-t bg-gray-50">
            <button
              type="button"
              onClick={onDelete}
              className="text-xs px-3 py-1.5 rounded-md border border-red-300 bg-white hover:bg-red-50 text-red-700 font-semibold flex items-center gap-1.5"
              data-testid="acc-detail-delete"
            >
              <Trash2 className="w-3.5 h-3.5" /> Unterkunft löschen
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center gap-1.5"
              data-testid="acc-detail-edit"
            >
              <Edit2 className="w-3.5 h-3.5" /> Bearbeiten
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
        {title}
      </div>
      <div className="rounded-md bg-white border border-gray-200 divide-y divide-gray-100">
        {children}
      </div>
    </section>
  );
}

function DetailRow({ icon: Icon, label, value, link, mono }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
      <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <span className="text-gray-500 w-32 flex-shrink-0">{label}</span>
      {link ? (
        <a href={link} className={`text-blue-600 hover:underline truncate ${mono ? "font-mono" : ""}`}>{value}</a>
      ) : (
        <span className={`text-gray-800 truncate ${mono ? "font-mono" : ""}`}>{value}</span>
      )}
    </div>
  );
}

/* ───────────────────────── PDF Quick Preview Overlay ───────────────────────── */

function PdfPreviewOverlay({ acc, projectId, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPdf = (acc.document_mime || acc.document_filename || "").toLowerCase().includes("pdf");

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    let urlRef = null;
    (async () => {
      if (!acc?.document_path) { setError("Kein Beleg hinterlegt"); setLoading(false); return; }
      try {
        const r = await authFetch(`${API}/projects/${projectId}/accommodations/source-file?path=${encodeURIComponent(acc.document_path)}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        urlRef = URL.createObjectURL(blob);
        setBlobUrl(urlRef);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Datei konnte nicht geladen werden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (urlRef) URL.revokeObjectURL(urlRef);
    };
  }, [acc, projectId]);

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="acc-pdf-preview-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 p-3 border-b bg-gray-50">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-800 truncate">
              {acc.document_filename || "Booking-Beleg"}
            </span>
            <span className="text-[10px] text-gray-500 truncate hidden sm:inline">
              · {acc.name}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex-shrink-0"
            title="Schließen"
            data-testid="acc-pdf-preview-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-hidden">
          {loading && (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 text-sm gap-2">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Beleg wird geladen…</span>
            </div>
          )}
          {error && !loading && (
            <div className="w-full h-full flex flex-col items-center justify-center text-red-600 text-sm gap-2 p-4 text-center">
              <X className="w-6 h-6" />
              <span>Fehler beim Laden: {error}</span>
            </div>
          )}
          {!loading && !error && blobUrl && (
            isPdf ? (
              <iframe
                src={blobUrl}
                title="Booking PDF"
                className="w-full h-full border-0"
                data-testid="acc-pdf-preview-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-2">
                <img
                  src={blobUrl}
                  alt={acc.document_filename || "Beleg"}
                  className="max-w-full max-h-full object-contain"
                  data-testid="acc-pdf-preview-image"
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
